import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ShieldCheck } from "lucide-react";
import { submitEnrollment } from "../lib/api";
import { SIGNATURE_PROGRAMS } from "../lib/signaturePrograms";

export default function Enroll() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    guardian_name: "", guardian_email: "", guardian_phone: "",
    child_first_name: "", child_age: 10, grade: "", school: "",
    preferred_program: "change-choices", preferred_contact: "email",
    notes: "", consent_participation: false, consent_media: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_participation) {
      toast.error("Please acknowledge the guardian participation consent to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await submitEnrollment({ ...form, child_age: Number(form.child_age) });
      toast.success("Thank you! An intake team member will reach out shortly.");
      setTimeout(() => nav("/"), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="enroll-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/35 via-transparent to-transparent" />
        <div className="container-x relative py-20 md:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Enrollment Inquiry</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl font-bold leading-[1.02]">
              Enroll your child.<br />
              <span className="italic text-[#F5B90A]">Start the next chapter.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/80">
              Share a few details about your family and the program you're interested in. An intake team member will reach out
              to schedule a conversation. This inquiry is confidential.
            </p>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="bg-white py-16 md:py-20" data-testid="enroll-form-section">
        <div className="container-x max-w-3xl mx-auto">
          <form onSubmit={submit} className="grid gap-5 bg-[#F7F3EC] rounded-lg p-8" data-testid="enroll-form">
            <h3 className="font-display text-2xl font-bold">Guardian Information</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="text-sm font-semibold">Guardian Name *</span>
                <input required value={form.guardian_name} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="enroll-guardian-name" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold">Guardian Email *</span>
                <input required type="email" value={form.guardian_email} onChange={(e) => setForm({ ...form, guardian_email: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="enroll-guardian-email" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold">Phone</span>
                <input value={form.guardian_phone} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="enroll-guardian-phone" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold">Preferred Contact</span>
                <select value={form.preferred_contact} onChange={(e) => setForm({ ...form, preferred_contact: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="enroll-contact-pref">
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="text">Text</option>
                </select>
              </label>
            </div>

            <div className="h-px bg-black/10 my-2" />

            <h3 className="font-display text-2xl font-bold">Child Information</h3>
            <p className="text-xs text-black/60 -mt-3">We only collect what's needed to connect. Sensitive medical, mental health, or behavioral information should not be shared on this form.</p>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="text-sm font-semibold">Child's First Name *</span>
                <input required value={form.child_first_name} onChange={(e) => setForm({ ...form, child_first_name: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="enroll-child-name" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold">Age *</span>
                <input required type="number" min={5} max={17} value={form.child_age} onChange={(e) => setForm({ ...form, child_age: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="enroll-child-age" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold">Grade</span>
                <input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="enroll-grade" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold">School</span>
                <input value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="enroll-school" />
              </label>
            </div>
            <label className="block">
              <span className="text-sm font-semibold">Preferred Program</span>
              <select value={form.preferred_program} onChange={(e) => setForm({ ...form, preferred_program: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="enroll-program">
                {SIGNATURE_PROGRAMS.map((p) => <option key={p.slug} value={p.slug}>{p.title}</option>)}
                <option value="unsure">I'm not sure — help me choose</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Anything else we should know? (optional)</span>
              <textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="enroll-notes" />
            </label>

            <div className="h-px bg-black/10 my-2" />

            <h3 className="font-display text-2xl font-bold">Guardian Acknowledgment</h3>
            <label className="flex items-start gap-3 rounded-lg bg-white p-4 border border-black/10 cursor-pointer" data-testid="enroll-consent-part">
              <input type="checkbox" required checked={form.consent_participation} onChange={(e) => setForm({ ...form, consent_participation: e.target.checked })} className="mt-1" />
              <span className="text-sm">
                I am the parent or legal guardian of this child and consent to Change Center Mentoring Group contacting us about program participation. I understand that enrollment is subject to intake, availability, and program-specific requirements.
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-lg bg-white p-4 border border-black/10 cursor-pointer" data-testid="enroll-consent-media">
              <input type="checkbox" checked={form.consent_media} onChange={(e) => setForm({ ...form, consent_media: e.target.checked })} className="mt-1" />
              <span className="text-sm">
                <strong>(Optional)</strong> I consent to Change Center using approved photos of my child in organizational communications. This is <em>separate</em> from program participation and can be given or withdrawn at any time.
              </span>
            </label>

            <button disabled={submitting} className="btn-primary mt-2" data-testid="enroll-submit">
              {submitting ? "Submitting..." : "Submit Enrollment Inquiry"}
            </button>
            <p className="text-xs text-black/60 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Your information is confidential and only used for enrollment follow-up.</p>
          </form>
        </div>
      </section>
    </>
  );
}
